import { keyframes } from "@mui/system";

/** Soft specular band that sweeps across the CTA. */
export const premiumCtaShineSweep = keyframes`
  0%, 62% { transform: translateX(-140%) skewX(-18deg); opacity: 0; }
  68% { opacity: 0.85; }
  82% { transform: translateX(220%) skewX(-18deg); opacity: 0; }
  100% { transform: translateX(220%) skewX(-18deg); opacity: 0; }
`;

/** Gentle breathing glow for teal / green primary settings CTAs. */
export const premiumCtaBreathTeal = keyframes`
  0%, 100% {
    box-shadow:
      0 6px 18px rgba(46, 196, 182, 0.28),
      0 2px 8px rgba(59, 166, 102, 0.22),
      inset 0 1px 0 rgba(255, 255, 255, 0.28);
  }
  50% {
    box-shadow:
      0 10px 26px rgba(46, 196, 182, 0.4),
      0 4px 12px rgba(59, 166, 102, 0.3),
      inset 0 1px 0 rgba(255, 255, 255, 0.38);
  }
`;

/**
 * Shared premium CTA motion: contained shine, soft pulse, press scale.
 * Merge onto a filled Button `sx` (settings purchase / sign-in).
 *
 * @param {{ animate?: boolean, borderRadius?: string|number }} [options]
 */
export function premiumCtaEffectSx({ animate = true, borderRadius = "10px" } = {}) {
    return {
        position: "relative",
        overflow: "hidden",
        isolation: "isolate",
        borderRadius,
        WebkitTapHighlightColor: "transparent",
        transition: "transform 0.14s ease, filter 0.18s ease, box-shadow 0.18s ease",
        ...(animate
            ? {
                  animation: `${premiumCtaBreathTeal} 3.6s ease-in-out infinite`,
              }
            : {
                  animation: "none",
              }),
        "&::after": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            width: "38%",
            height: "100%",
            pointerEvents: "none",
            zIndex: 1,
            background:
                "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.5) 48%, transparent 100%)",
            opacity: 0,
            ...(animate
                ? {
                      animation: `${premiumCtaShineSweep} 4.2s ease-in-out infinite`,
                  }
                : {
                      animation: "none",
                  }),
        },
        "& > .MuiCircularProgress-root, & > span, & .MuiButton-label": {
            position: "relative",
            zIndex: 2,
        },
        "&:hover": {
            filter: "brightness(1.04)",
        },
        "&:active:not(.Mui-disabled)": {
            transform: "scale(0.96)",
            filter: "brightness(0.98)",
            animation: "none",
            "&::after": {
                animation: "none",
                opacity: 0,
            },
        },
        "&.Mui-disabled": {
            animation: "none",
            filter: "none",
            "&::after": {
                animation: "none",
                opacity: 0,
            },
        },
        "@media (prefers-reduced-motion: reduce)": {
            animation: "none",
            "&::after": {
                animation: "none",
                opacity: 0,
            },
            "&:active:not(.Mui-disabled)": {
                transform: "scale(0.96)",
            },
        },
    };
}

/** Filled teal primary button base used on Settings IAPs / Sign In. */
export function settingsPrimaryButtonSx({ animate = true, accent = "#2EC4B6" } = {}) {
    return {
        ...premiumCtaEffectSx({ animate, borderRadius: "12px" }),
        fontFamily:
            '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Segoe UI", sans-serif',
        textTransform: "none",
        fontSize: "1.0625rem",
        fontWeight: 600,
        py: 1.5,
        minHeight: 48,
        color: "#ffffff",
        background: `linear-gradient(185deg, ${accent} 0%, #3BA666 55%, #2e7d32 100%)`,
        border: "1px solid rgba(255, 255, 255, 0.22)",
        boxShadow: "0 6px 18px rgba(46, 196, 182, 0.28)",
        "&:hover": {
            background: `linear-gradient(185deg, ${accent} 0%, #3BA666 55%, #2e7d32 100%)`,
            filter: "brightness(1.05)",
        },
        "&:active:not(.Mui-disabled)": {
            transform: "scale(0.96)",
            filter: "brightness(0.98)",
            animation: "none",
            "&::after": {
                animation: "none",
                opacity: 0,
            },
        },
        "&.Mui-disabled": {
            color: "rgba(255, 255, 255, 0.9)",
            background: "rgba(59, 166, 102, 0.4)",
            boxShadow: "none",
            border: "1px solid transparent",
            animation: "none",
            filter: "none",
            "&::after": {
                animation: "none",
                opacity: 0,
            },
        },
    };
}
